import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useTheme from '@hero/branding/theme';
import AlertIcon from '@hero/ui-kit/icons/utility/AlertIcon';
import Container from '@hero/ui-kit/layout/Container';
import P from '@hero/ui-kit/typography/P';
import HighlightedText from '../../../components/HighlightedText';
import { useUserStatusContext } from '../../../context/UserStatus';
import { GetOrganizationMembersParams } from '../api/useGetOrganizationMembers';
import CareManagerBadge from '../components/CareManagerBadge';
import MismatchFlag from '../components/MismatchFlag';
import { writeMemberListSnapshot } from '../OrganizationMemberDetails/MemberDetailsWrapper/useMemberListNeighbors';
import { OrganizationMemberList } from '../types';
import { getDeviceMismatch } from '../utils/deviceProfileMismatch';
import getMemberUrl from '../utils/getMemberUrl';
import disengagedDictionary from './disengagedDictionary';
import * as Style from './style.module.scss';
import DeviceSerial from '../OrganizationMemberList/DeviceSerial';
import DeviceStatus from '../OrganizationMemberList/DeviceStatus';
import getMemberRoleLabel from '../OrganizationMemberList/utils/getMemberRoleLabel';
import getMembershipLabel from '../OrganizationMemberList/utils/getMembershipLabel';
import { getBillingFormattedDate } from '../../../utils/date';
import { DisengagedColumnId } from './columns';

const ListItem: React.FC<{
    data: OrganizationMemberList;
    search: string;
    listQuery: GetOrganizationMembersParams;
    isColumnVisible: (id: DisengagedColumnId) => boolean;
    gridColumns: string;
}> = ({ data, search, listQuery, isColumnVisible, gridColumns }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const canOpenDetails = useUserStatusContext().permissions.fleet.fleetMemberView;

    const { hasMismatch, content: mismatchContent } = getDeviceMismatch({
        firstName: data.first_name,
        lastName: data.last_name,
        dateOfBirth: data.date_of_birth,
        deviceFirstName: data.fre_first_name,
        deviceLastName: data.fre_last_name,
        deviceDateOfBirth: data.fre_dob
    });

    const alert = data.disengaged_reason === 'DISCONNECTED' || hasMismatch;

    const hasId: boolean = React.useMemo(() => {
        return !!data.member_id || !!data.hero_user_id;
    }, [data]);

    const handleNavigate = React.useCallback(
        (e: React.SyntheticEvent) => {
            if (!e.isDefaultPrevented() && hasId && canOpenDetails) {
                const from = `${location.pathname}${location.search}`;
                const snapshot = { from, listQuery, listKind: 'members' as const };
                writeMemberListSnapshot(snapshot);
                navigate(`${getMemberUrl(data.hero_user_id, data.member_id)}`, {
                    state: snapshot
                });
            }
        },
        [hasId, data, navigate, canOpenDetails, location, listQuery]
    );

    return (
        <div
            onClick={handleNavigate}
            onKeyDown={() => null}
            tabIndex={0}
            role="button"
            style={{ cursor: hasId ? 'pointer' : 'not-allowed' }}
            className={Style.memberListWrapper}
        >
            <Container
                gridTemplateColumns={gridColumns}
                gridColumnGap="small"
                className={Style.container}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'start',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}
                >
                    <P noDefaultMargin strong>
                        <HighlightedText
                            highlight={search}
                            text={
                                data.first_name || data.last_name
                                    ? `${data.first_name} ${data.last_name}`
                                    : 'N/A'
                            }
                        />
                    </P>
                    {data.email ? (
                        <P
                            size="small"
                            noDefaultMargin
                            styles={{
                                color: theme.colors.neutrals.textGamma
                            }}
                        >
                            <HighlightedText highlight={search} text={data.email} />
                        </P>
                    ) : (
                        ''
                    )}
                </div>

                {isColumnVisible('patientId') && (
                    <div className="flex-center-vertical">
                        <P noDefaultMargin>
                            <HighlightedText
                                highlight={search}
                                text={data.external_user_id || 'N/A'}
                            />
                        </P>
                    </div>
                )}

                {isColumnVisible('memberSince') && (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <P noDefaultMargin styles={{ color: theme.colors.neutrals.textGamma }}>
                            {data.activated_at
                                ? getBillingFormattedDate(data.activated_at, 'MMM d, yyyy')
                                : 'Not Activated Yet'}
                        </P>
                    </div>
                )}

                {isColumnVisible('primaryReason') && (
                    <P noDefaultMargin strong styles={{ display: 'flex', alignItems: 'center' }}>
                        {data.disengaged_reason &&
                            disengagedDictionary[data.disengaged_reason].label}
                    </P>
                )}

                {isColumnVisible('secondaryReasons') && (
                    <P noDefaultMargin styles={{ display: 'flex', alignItems: 'center' }}>
                        {data.other_disengaged_reasons?.length &&
                            data.other_disengaged_reasons
                                .map((reason) => disengagedDictionary[reason].label)
                                .join(', ')}
                    </P>
                )}

                {isColumnVisible('careManager') && (
                    <div className="flex-center-vertical">
                        <CareManagerBadge careManagerIds={data.care_manager_ids || null} />
                    </div>
                )}

                {isColumnVisible('device') && (
                    <div className="flex-center-vertical">
                        <DeviceSerial
                            deviceInfo={data.device_info || undefined}
                            search={search}
                            exchangeDeviceInfo={data.exchange_device_info || undefined}
                        />
                    </div>
                )}

                {isColumnVisible('status') && (
                    <DeviceStatus
                        deviceInfo={data.device_info || undefined}
                        exchangeDeviceInfo={data.exchange_device_info || undefined}
                    />
                )}

                {isColumnVisible('membership') && (
                    <div className="flex-center-vertical">
                        <P noDefaultMargin>{getMembershipLabel(data.member_status)}</P>
                    </div>
                )}

                {isColumnVisible('role') && (
                    <div className="flex-center-vertical">
                        <P noDefaultMargin>{getMemberRoleLabel(data.member_role)}</P>
                    </div>
                )}

                {alert && (
                    <div className={Style.iconColumn}>
                        {hasMismatch && mismatchContent ? (
                            <MismatchFlag content={mismatchContent} placement="left" />
                        ) : (
                            <AlertIcon size="utility" />
                        )}
                    </div>
                )}
            </Container>
        </div>
    );
};

export default ListItem;
